/*
 *   File :  basket-order-detail.js
 *   Author : https://evoqins.com
 *   Description : Index file to import components
 *   Integrations : 
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Components
import { TextButton } from "../../Component/Buttons";
import { AlphaBasketCard, ProfileCard } from "../../Component/Cards";
import { Icon } from "../../Component/Icon";
import { APIService } from "../../Service";
import { Loader } from "../../Component/Others";
import { _scrollToTop } from "../../Helper";

const DEFAULT_SORT = {
    order_stock_name: "ASC" // order detail
};

function BasketOrderDetail() {

    const navigate = useNavigate();
    const { state } = useLocation();
    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const sortBy = useRef(DEFAULT_SORT);

    const [dataLoading, setDataLoading] = useState(true);
    const [orderDetail, setOrderDetail] = useState({});
    const [expandedKey, setExpandedKey] = useState(-1);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();

        // API call
        if (state && state.portfolio_id) {
            _getOrderDetail();
        } else {
            navigate(-1);
        }
    }, []);

    // handle format amount 
    const _formattedAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(amount);
    };

    // handle expand stocks data
    const _handleShowStocksDetail = (key) => {
        if (key === expandedKey) {
            setExpandedKey(-1);
        } else {
            setExpandedKey(key);
        }
    };

    //  API  -  Get order list data
    const _getOrderDetail = async () => {
        let url = "portfolio/orders/detail";
        let data = {
            "portfolio_id": state.portfolio_id,
            "user_id": USER_DATA.unique_identifier
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setOrderDetail(response.data);
        } else {
            navigate('/orders');
        }

        setDataLoading(false);
    };


    return (
        <div className="row mb-5">
            <div className="col-12">
                <TextButton name="Order Details"
                    icon={true}
                    labelClass="e-font-16-res"
                    type={2}
                    onPress={() => navigate("/orders")} />
            </div>
            {
                dataLoading ?
                    <Loader wrapperClassName="min-vh-100 margin-100px-top" />
                    :
                    <div className="row mt-4 pe-0 flex-column flex-md-row gap-3 gap-md-0">
                        <div className="order-2 order-md-1 col-12 col-md-7 pe-0 pe-sm-3">
                            <div className="p-3 card-box-shadow h-100 e-bg-white">
                                <div className='col-12 col-sm-12'>
                                    <AlphaBasketCard product={orderDetail}
                                        type={2} />
                                    <div className="mt-4">
                                        <h6 className="e-text-black e-primary-font-clash-display e-font-weight-500 e-font-16 line-height-24px">
                                            Orders
                                        </h6>
                                        {/* First order */}
                                        <div className="d-flex flex-column gap-3">
                                            {
                                                orderDetail.trade_data.map((item, index) => (
                                                    <div key={index}
                                                        className="e-border-1px-anti-flash-white border-radius-8 pt-3 cursor-pointer">
                                                        {/* Order summary section */}
                                                        <div className="px-3 e-bg-primary-color" onClick={() => _handleShowStocksDetail(index)}>
                                                            <div className="d-flex justify-content-between">
                                                                <div className='col-6'>
                                                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                        Traded on
                                                                    </p>
                                                                    <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px`}>
                                                                        {item.traded_on}
                                                                    </p>
                                                                </div>
                                                                <div className='col-6'>
                                                                    <div className="d-flex justify-content-between">
                                                                        <div>
                                                                            <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                Order Status
                                                                            </p>
                                                                            <p className={`d-flex gap-1 align-items-center mb-0 e-alt-font-lato e-font-16 e-font-weight-600 line-height-16px
                                                                                ${item.order_status === "Success" ? "e-text-go-green" : item.order_status === "Partially Completed" ? "e-text-marigold" : "e-text-paradise-pink"}`}>
                                                                                <Icon icon={item.order_status === "Success" ? "success-icon" : item.order_status === "Partially Completed" ? "processing-icon" : "failed-icon"}
                                                                                    size={18} />
                                                                                {item.order_status}
                                                                            </p>
                                                                        </div>
                                                                        <div className="align-self-end">
                                                                            <span className="e-bg-magnolia-light rounded-5 px-1 cursor-pointer"
                                                                                onClick={() => _handleShowStocksDetail(index)}>
                                                                                <Icon icon="drop-down-icon"
                                                                                    className={`${expandedKey === index ? "e-rotate-180" : ""} mb-1 transition`}
                                                                                    size={12} />
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className={`mt-3 ${expandedKey === index ? "overflow-scroll no-scrollbar" : "h-0 overflow-hidden"}`}>
                                                            <div className="">
                                                                <div className="d-flex py-2">
                                                                    <div className="ps-3 col-5 col-sm-4 py-2 e-bg-magnolia-light" >
                                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                                            {item.trade_info.length === 1 ? "Stock" : "Stocks"} ({item.trade_info.length})
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-3 col-sm-2  text-center py-2 e-bg-magnolia-light">
                                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                                            Quantity
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-4 col-sm-3  text-center py-2 e-bg-magnolia-light">
                                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                                            Transaction Value
                                                                        </span>
                                                                    </div>
                                                                    {/* <div className="col-5 col-sm-4 col-md-3 col-lg-2  text-center py-2 e-bg-magnolia-light">
                                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                                            Order Type
                                                                        </span>
                                                                    </div> */}
                                                                    <div className="col-3 col-sm-2  text-center py-2 e-bg-magnolia-light">
                                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                                            Action
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-5 col-sm-4  col-lg-3 text-center py-2 e-bg-magnolia-light">
                                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                                            Status
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                {/* <div className={`${expandedKey === index ? "" : "h-0 overflow-hidden"}`}> */}
                                                                {
                                                                    item.trade_info.map((order_item, index) => (
                                                                        <div key={index}>
                                                                            <div className="d-flex py-2 align-items-center">
                                                                                <div className="ps-3 col-5 col-sm-4  ">
                                                                                    <span className="d-flex flex-column e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-14 line-height-20px">
                                                                                        {order_item.script_name}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="col-3 col-sm-2 text-center">
                                                                                    <span className="e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-14 line-height-20px e-letter-spacing-min-point-1px">
                                                                                        {order_item.quantity}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="col-4 col-sm-3  text-center">
                                                                                    <span className="e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-14 line-height-20px e-letter-spacing-min-point-1px">
                                                                                        {_formattedAmount(order_item.purchase_price)}
                                                                                    </span>
                                                                                </div>
                                                                                {/* <div className="col-5 col-sm-4 col-md-3 col-lg-2 text-center">
                                                                                    <span className="e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px e-letter-spacing-min-point-1px">
                                                                                        {order_item.operation}
                                                                                    </span>
                                                                                </div> */}
                                                                                <div className="col-3 col-sm-2 text-center">
                                                                                    <span className="e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px e-letter-spacing-min-point-1px">
                                                                                        {order_item.action}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="col-5 col-sm-4 col-lg-3 text-center">
                                                                                    <span className={`d-flex gap-1 align-items-center justify-content-center mb-0 e-alt-font-lato e-font-12 e-font-weight-600 line-height-16px
                                                                                        ${order_item.status === "COMPLETE" ? "e-text-go-green" : order_item.status === "PENDING" ? "e-text-marigold" : "e-text-paradise-pink"}`}>
                                                                                        <Icon icon={order_item.status === "COMPLETE" ? "success-icon" : order_item.status === "PENDING" ? "processing-icon" : "failed-icon"}
                                                                                            size={18} />
                                                                                        {order_item.status}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <hr className="mx-3 my-0 e-text-anti-flash-white opacity-100 w-100" />
                                                                        </div>

                                                                    ))
                                                                }
                                                                <div className="d-flex  py-2 mt-2">
                                                                    <div className="ps-3 col-5 col-sm-4">
                                                                        <span className="e-text-chinese-black e-alt-font-lato e-font-weight-600 e-font-14 line-height-20px e-letter-spacing-min-point-1px">
                                                                            Total
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-3 col-sm-2 text-center">
                                                                        <span className="e-font-14-res e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-16 line-height-20px e-letter-spacing-min-point-1px">
                                                                            {
                                                                                item.trade_info.map(item => Number(item.quantity)).reduce((a, b) => a + b, 0)
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-4 col-sm-3 text-center">
                                                                        <span className="e-font-14-res e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-16 line-height-20px e-letter-spacing-min-point-1px">
                                                                            {
                                                                                _formattedAmount(item.trade_info.map(item => Number(item.purchase_price)).reduce((a, b) => a + b, 0))
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 order-md-2 col-12 col-sm-8 col-md-5 pe-0">
                            <ProfileCard navigate={true} />
                        </div>
                    </div>
            }
        </div>
    )
}

export default BasketOrderDetail;